/**
 * Macro adjust for DXF vectors — drag the whole drawing into place.
 *
 * The automatic fit lands the drawing within ~1 m; the residual is a bias
 * component that text-to-text fitting provably cannot see (any offset
 * identical across every label is absorbed by the fit's own translation).
 * So the last metre is the user's, and this is the tool for it.
 *
 * Two things make it cheap:
 *  - DRAG FEEDBACK is a PAINT property (`line-translate` / `fill-translate`,
 *    viewport-anchored pixels), so a 70k-feature layer follows the cursor
 *    without a single data rebuild — the same "don't touch the data during
 *    a drag" lesson the label/grid drags taught.
 *  - COMMIT converts the screen delta into PAGE-space units once and stores
 *    it in `dxf.adjust`, which the mapper composes after the fit. One
 *    setData per drag, and re-fits underneath never destroy it.
 */

import { useEffect } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useMapStore } from '../../stores/mapStore';
import { inverseTransformPoint } from '../../domain/layout/transform';
import { IDENTITY_ADJUST } from '../../utils/dxfRegistration';
import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';

const DRAG_THRESHOLD_PX = 3;

/** Page-space delta for a screen-space drag, via the layout transform. */
function screenDeltaToPage(
  map: MapboxMap,
  from: { x: number; y: number },
  to: { x: number; y: number },
  layoutTransform: Parameters<typeof inverseTransformPoint>[0],
): [number, number] {
  const a = map.unproject([from.x, from.y]);
  const b = map.unproject([to.x, to.y]);
  const [ax, ay] = inverseTransformPoint(layoutTransform, a.lng, a.lat);
  const [bx, by] = inverseTransformPoint(layoutTransform, b.lng, b.lat);
  return [bx - ax, by - ay];
}

export default function useDxfAdjust(
  mapRef: React.RefObject<MapboxMap | null>,
  isMapReady: boolean,
) {
  const adjustId = useMapStore((s) => s.dxfAdjustOverlayId);

  useEffect(() => {
    const map = mapRef.current;
    if (!isMapReady || !map || !adjustId) return;

    const layerIdsFor = (): string[] => {
      const ids: string[] = [];
      try {
        for (const l of map.getStyle()?.layers ?? []) {
          if (l.id.startsWith('dxf-layer-line-') || l.id.startsWith('dxf-layer-fill-')) ids.push(l.id);
        }
      } catch { /* style mid-teardown */ }
      return ids;
    };

    const setTranslate = (dx: number, dy: number) => {
      for (const id of layerIdsFor()) {
        try {
          map.setPaintProperty(id, id.startsWith('dxf-layer-fill-') ? 'fill-translate' : 'line-translate', [dx, dy]);
        } catch { /* layer went away */ }
      }
    };

    let start: { x: number; y: number } | null = null;
    let dragging = false;

    const activeLayout = () => {
      const st = useProjectStore.getState();
      const overlay = st.data.overlays.find((o) => o.id === adjustId);
      const reg = overlay?.dxf?.registration;
      if (reg?.kind !== 'layout') return null;
      const layouts = st.data.siteLayouts ?? (st.data.siteLayout ? [st.data.siteLayout] : []);
      return layouts.find((l) => l.id === reg.layoutId)?.transform ?? null;
    };

    const onDown = (e: MapMouseEvent) => {
      if (e.originalEvent.button !== 0) return;
      start = { x: e.point.x, y: e.point.y };
      dragging = false;
      map.dragPan.disable();
    };

    const onMove = (e: MapMouseEvent) => {
      if (!start) return;
      const dx = e.point.x - start.x, dy = e.point.y - start.y;
      if (!dragging && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      dragging = true;
      setTranslate(dx, dy);
    };

    const onUp = (e: MapMouseEvent) => {
      const from = start;
      start = null;
      map.dragPan.enable();
      if (!from || !dragging) return;
      dragging = false;
      setTranslate(0, 0);
      const tf = activeLayout();
      if (!tf) return;
      const [pdx, pdy] = screenDeltaToPage(map, from, e.point, tf);
      const st = useProjectStore.getState();
      const overlay = st.data.overlays.find((o) => o.id === adjustId);
      if (!overlay?.dxf) return;
      const cur = overlay.dxf.adjust ?? IDENTITY_ADJUST;
      st.updateOverlay(adjustId, {
        dxf: { ...overlay.dxf, adjust: { ...cur, dx: cur.dx + pdx, dy: cur.dy + pdy } },
      });
    };

    // Arrow keys nudge; Escape leaves the mode. 0.25 m normally, 1 m with
    // Shift — the two scales an as-built correction actually needs.
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') { useMapStore.getState().setDxfAdjustOverlay(null); return; }
      const dirs: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1],
      };
      const d = dirs[ev.key];
      if (!d) return;
      const tf = activeLayout();
      if (!tf) return;
      ev.preventDefault();
      // metres → page points via the layout scale
      const m = tf.matrix;
      const metresPerPt = Math.hypot(m[0], m[3]);
      const step = (ev.shiftKey ? 1 : 0.25) / (metresPerPt || 1);
      const st = useProjectStore.getState();
      const overlay = st.data.overlays.find((o) => o.id === adjustId);
      if (!overlay?.dxf) return;
      const cur = overlay.dxf.adjust ?? IDENTITY_ADJUST;
      st.updateOverlay(adjustId, {
        dxf: { ...overlay.dxf, adjust: { ...cur, dx: cur.dx + d[0] * step, dy: cur.dy + d[1] * step } },
      });
    };

    map.on('mousedown', onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    window.addEventListener('keydown', onKey);
    const canvas = map.getCanvas();
    const prevCursor = canvas.style.cursor;
    canvas.style.cursor = 'move';

    return () => {
      map.off('mousedown', onDown);
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      window.removeEventListener('keydown', onKey);
      canvas.style.cursor = prevCursor;
      map.dragPan.enable();
      setTranslate(0, 0);
    };
  }, [isMapReady, mapRef, adjustId]);
}
